import React, { useCallback, useEffect, useState } from 'react';
import { SimpleGrid, useToast } from '@chakra-ui/react';
import { MdBadge, MdFileDownload, MdPendingActions, MdPeopleAlt, MdStorefront } from 'react-icons/md';
import {
  listAppUsers,
  exportAppUsersExcel,
} from '../../../repositories/appUser.repository';
import { getDashboardCounts } from '../../../repositories/dashboard.repository';
import { toApiStatus } from '../../../models/_helpers';
import AppUserDetailPage from '../users/detail';
import {
  HubCard,
  HubError,
  HubFilterBar,
  HubGhostButton,
  HubKycBadge,
  HubLoading,
  HubMetricCard,
  HubTable,
  HubThumb,
  useHubHeaderActions,
} from './hubUi';

function displayName(u) {
  return (
    u.fullName ||
    u.tradeName ||
    [u.firstName, u.middleName, u.lastName].filter(Boolean).join(' ') ||
    u.phone ||
    `User #${u.id}`
  );
}

export function HubMembersHub({ push }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await getDashboardCounts());
    } catch (e) {
      setError(e.message || 'Failed to load user counts');
      toast({ title: e.message || 'Failed to load user counts', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;

  const vendors = data?.vendors || {};
  const sellers = vendors.serviceProviders?.total ?? 0;
  const buyers = vendors.agencies?.total ?? 0;
  const total = vendors.total ?? sellers + buyers;
  const pending = vendors.pending ?? 0;
  const sellerPending = vendors.serviceProviders?.pending ?? 0;
  const buyerPending = vendors.agencies?.pending ?? 0;

  return (
    <>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
        <HubMetricCard
          icon={MdPeopleAlt}
          label="All users"
          total={String(total)}
          metrics={[
            { label: 'Sellers', value: String(sellers) },
            { label: 'Buyers', value: String(buyers) },
            { label: 'Pending', value: String(pending) },
          ]}
          onClick={() => push({ id: 'memberList', label: 'All users', filter: 'all' })}
        />
        <HubMetricCard
          icon={MdStorefront}
          label="Sellers"
          total={String(sellers)}
          metrics={[
            { label: 'Pending', value: String(sellerPending) },
            { label: 'Verified', value: String(vendors.serviceProviders?.verified ?? 0) },
            { label: 'Blocked', value: String(vendors.serviceProviders?.blocked ?? 0) },
          ]}
          onClick={() =>
            push({ id: 'memberList', label: 'Sellers', filter: 'seller' })
          }
        />
        <HubMetricCard
          icon={MdBadge}
          label="Buyers"
          total={String(buyers)}
          metrics={[
            { label: 'Pending', value: String(buyerPending) },
            { label: 'Verified', value: String(vendors.agencies?.verified ?? 0) },
            { label: 'Blocked', value: String(vendors.agencies?.blocked ?? 0) },
          ]}
          onClick={() => push({ id: 'memberList', label: 'Buyers', filter: 'buyer' })}
        />
        <HubCard
          icon={MdPendingActions}
          label="Pending Verification"
          value={String(pending)}
          hint="Needs verification"
          onClick={() =>
            push({
              id: 'memberList',
              label: 'Pending Verification',
              filter: 'pending',
            })
          }
        />
      </SimpleGrid>
    </>
  );
}

export function HubMemberList({ push, filter = 'all' }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(
    filter === 'seller' ? 'seller' : filter === 'buyer' ? 'buyer' : '',
  );
  const [statusFilter, setStatusFilter] = useState(
    filter === 'pending' ? 'Pending' : '',
  );
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const userType =
        roleFilter === 'seller'
          ? 'service_provider'
          : roleFilter === 'buyer'
            ? 'agency'
            : undefined;
      const res = await listAppUsers({
        pageSize: 200,
        userType,
        verificationStatus: statusFilter
          ? toApiStatus(statusFilter)
          : undefined,
        search: search.trim() || undefined,
      });
      setItems(res.items || []);
    } catch (e) {
      toast({ title: e.message || 'Failed to load users', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast, roleFilter, statusFilter, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportAppUsersExcel({
        search: search.trim() || undefined,
        userType:
          roleFilter === 'seller'
            ? 'service_provider'
            : roleFilter === 'buyer'
              ? 'agency'
              : undefined,
        verificationStatus: statusFilter
          ? toApiStatus(statusFilter)
          : undefined,
      });
      toast({ title: 'Export downloaded', status: 'success' });
    } catch (e) {
      toast({ title: e.message || 'Export failed', status: 'error' });
    } finally {
      setExporting(false);
    }
  };

  useHubHeaderActions(
    () => (
      <HubGhostButton
        leftIcon={<MdFileDownload />}
        onClick={handleExport}
        isLoading={exporting}
      >
        Export Excel
      </HubGhostButton>
    ),
    [exporting],
  );

  return (
    <>
      <HubFilterBar
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search name, phone, village…"
        filters={[
          {
            key: 'role',
            value: roleFilter,
            onChange: setRoleFilter,
            options: [
              { value: '', label: 'All roles' },
              { value: 'seller', label: 'Seller' },
              { value: 'buyer', label: 'Buyer' },
            ],
          },
          {
            key: 'status',
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
              { value: '', label: 'All verification' },
              { value: 'Pending', label: 'Pending' },
              { value: 'Verified', label: 'Verified' },
              { value: 'Rejected', label: 'Rejected' },
            ],
          },
        ]}
        onClear={() => {
          setSearch('');
          setRoleFilter('');
          setStatusFilter('');
        }}
      />
      <HubTable
        loading={loading}
        rows={items}
        onRowClick={(r) =>
          push({ id: 'memberDetail', label: displayName(r), itemId: r.id })
        }
        columns={[
          {
            key: 'photo',
            label: '',
            render: (r) => (
              <HubThumb src={r.profilePhotoUrl} label={displayName(r)} />
            ),
          },
          {
            key: 'name',
            label: 'Name',
            render: (r) => displayName(r),
          },
          { key: 'userTypeLabel', label: 'Role' },
          { key: 'phone', label: 'Phone' },
          { key: 'village', label: 'Village' },
          {
            key: 'verificationStatus',
            label: 'Verification',
            render: (r) => (
              <HubKycBadge status={r.verificationStatus}>
                {r.verificationStatus}
              </HubKycBadge>
            ),
          },
        ]}
      />
    </>
  );
}

/** Classic member detail UI embedded inside Hub. */
export function HubMemberDetail({ push, itemId }) {
  return (
    <AppUserDetailPage
      userId={itemId}
      embedded
      onOpenIndent={(requestId) =>
        push({
          id: 'indentDetail',
          label: `REQ-${requestId}`,
          itemId: requestId,
        })
      }
      onOpenEquipment={(ueId) =>
        push({
          id: 'equipmentDetail',
          label: `UE-${ueId}`,
          itemId: ueId,
        })
      }
      onOpenMember={(id) => {
        if (Number(id) === Number(itemId)) return;
        push({
          id: 'memberDetail',
          label: `User #${id}`,
          itemId: id,
        });
      }}
    />
  );
}
